import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import { DeleteButton } from '@/shared/ui/list/DeleteButton';
import { FormModalButton, type EntityField, type EntityValues } from '@/shared/ui/list/EntityFormModal';
import { postListFetcher } from '@/shared/api/list';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import type { ListPageConfig, ListRow } from '@/shared/ui/list/types';
import { deleteUtm, saveUtm } from './api';

const LIST_KEY = 'marketing-utm';

interface Utm extends ListRow {
  id: number;
  key: string | { utm_source?: string; utm_medium?: string } | null;
  title: string;
  create_time?: number;
  update_time?: number;
  is_active?: number;
}

const FIELDS: EntityField[] = [
  { name: 'utmSource', label: 'utm_source', placeholder: 'Например, yandex', errorKey: 'key' },
  { name: 'utmMedium', label: 'utm_medium', placeholder: 'Например, cpc', errorKey: 'key' },
  { name: 'title', label: 'Title', placeholder: 'Название...' },
  { name: 'isActive', label: 'Is active', type: 'checkbox', placeholder: 'Флаг активности' },
];

const toKeyObject = (key: Utm['key']) => (key && typeof key === 'object' ? key : {});

const formatKey = (key: Utm['key']) =>
  key && typeof key === 'object' ? Object.values(key).filter(Boolean).join(' / ') : key;

const toValues = (utm?: Utm): EntityValues => {
  const key = toKeyObject(utm?.key ?? null);
  return {
    utmSource: key.utm_source ?? '',
    utmMedium: key.utm_medium ?? '',
    title: utm?.title ?? '',
    isActive: utm ? Boolean(Number(utm.is_active)) : true,
  };
};

const saveValues = (id?: number) => (v: EntityValues) =>
  saveUtm({
    id,
    utmSource: String(v.utmSource),
    utmMedium: String(v.utmMedium),
    title: String(v.title),
    isActive: Boolean(v.isActive),
  });

const config: ListPageConfig<Utm> = {
  queryKey: LIST_KEY,
  title: 'Динамические заголовки',
  errorText: 'Ошибка загрузки UTM',
  fetchList: postListFetcher('/utm/list/'),
  rows: ['items'],
  searchKeys: [{ value: 'title', label: 'Поиск по title' }],
  headerActions: (
    <FormModalButton
      buttonLabel="Создать"
      buttonClassName="btn btn-info pull-right"
      title="Создать UTM"
      fields={FIELDS}
      initial={toValues()}
      save={saveValues()}
      listKey={LIST_KEY}
    />
  ),
  columns: [
    { header: 'id', render: (utm) => utm.id },
    { header: 'key', render: (utm) => formatKey(utm.key) },
    { header: 'title', render: (utm) => utm.title },
    { header: 'create_time', render: (utm) => formatUnixDateTime(utm.create_time) || 'No create time' },
    { header: 'update_time', render: (utm) => formatUnixDateTime(utm.update_time) || 'No update time' },
    { header: 'is_active', render: (utm) => utm.is_active },
    {
      header: 'update',
      render: (utm) => (
        <FormModalButton
          buttonLabel="Обновить"
          title="Обновить UTM"
          fields={FIELDS}
          initial={toValues(utm)}
          save={saveValues(utm.id)}
          listKey={LIST_KEY}
        />
      ),
    },
    {
      header: 'delete',
      render: (utm) => <DeleteButton listKey={LIST_KEY} remove={() => deleteUtm(utm.id)} />,
    },
  ],
};

export const UtmPage = () => <GenericListPage config={config} />;
